import { type JSX } from "react";
import { DEFAULT_OFFER_TEXTS } from "@/lib/offerDocumentDefaults";
import { estimateVatSummary } from "@/utils/mvpEstimateMath";
import { fmtPln, formatPlDate, payableLines } from "@/components/offer/offerDocumentUtils";
import type { OfferData, OfferSectionProps } from "@/components/offer/offerDocumentTypes";
import styles from "./CoverSection.module.css";

function visibleText(value: string | undefined): string {
  return value?.trim() ?? "";
}

function shouldHideValue(value: string | undefined): boolean {
  const normalized = visibleText(value).toLowerCase();
  return !normalized || normalized === "none" || normalized === "hidden" || normalized === "nie pokazuj";
}

function buildProjectTitle(offerData: OfferData): string {
  return (
    visibleText(offerData.clientName) ||
    visibleText(offerData.clientAddress) ||
    visibleText(offerData.offerTexts?.title) ||
    "Oferta remontowa"
  );
}

function buildTimelineText(offerData: OfferData): string {
  return visibleText(offerData.offerTexts?.timelineNote) || DEFAULT_OFFER_TEXTS.timelineNote || "Do ustalenia";
}

function buildWarrantyText(offerData: OfferData): string {
  if (shouldHideValue(offerData.warrantyType)) return "Do ustalenia";
  return visibleText(offerData.warrantyType) || "Gwarancja wykonawcy";
}

export function CoverSection({ offerData }: OfferSectionProps): JSX.Element {
  const brandName = visibleText(offerData.companyName) || "Wykonawca";
  const companyLogoUrl = visibleText(offerData.companyLogoUrl);
  const projectTitle = buildProjectTitle(offerData);
  const clientAddress = visibleText(offerData.clientAddress);
  const showAddress = Boolean(clientAddress && clientAddress !== projectTitle);
  const vat = estimateVatSummary(payableLines(offerData.lines));

  return (
    <section className={styles.coverPage} aria-label="Okładka oferty reprezentacyjnej">
      <div className={styles.accentBar} aria-hidden="true" />
      <div className={styles.coverContent}>
        <header className={styles.brandRow}>
          {companyLogoUrl ? (
            <img className={styles.logo} src={companyLogoUrl} alt={brandName} />
          ) : null}
          <span className={styles.companyName}>{brandName}</span>
        </header>

        <div className={styles.heroBlock}>
          <p className={styles.kicker}>OFERTA REPREZENTACYJNA</p>
          <h1 className={styles.projectTitle}>{projectTitle}</h1>
          {showAddress ? <p className={styles.projectAddress}>{clientAddress}</p> : null}
          <div className={styles.separator} aria-hidden="true" />
          <p className={styles.valueLabel}>Wartość oferty</p>
          <p className={styles.totalPrice}>{fmtPln(vat.grossTotal)} PLN</p>
        </div>

        <dl className={styles.metaRow}>
          <div className={styles.metaItem}>
            <dt>Data</dt>
            <dd>{formatPlDate(offerData.offerDate)}</dd>
          </div>
          <div className={styles.metaItem}>
            <dt>Gwarancja</dt>
            <dd>{buildWarrantyText(offerData)}</dd>
          </div>
          <div className={styles.metaItem}>
            <dt>Termin</dt>
            <dd>{buildTimelineText(offerData)}</dd>
          </div>
        </dl>
      </div>
      <div className={styles.accentBar} aria-hidden="true" />
    </section>
  );
}
